import { useMemo, useState } from 'react'
import { PanelError, toPanelError } from '@/console/api/errors'
import { DefaultPriorityGridRequest, DefaultPriorityGridResponse } from '@/console/api/types'
import { requestSetDefaultPriorityGrid } from '@/console/data/defaultPriorityGrid'
import { useReportBusy } from '@/console/state/useReportBusy'
import { Button } from '@/console/ui/Button'
import { Callout } from '@/console/ui/Callout'
import { ConfirmDialog } from '@/console/ui/ConfirmDialog'
import { DataTable } from '@/console/ui/DataTable'
import { Field } from '@/console/ui/Field'
import { Note } from '@/console/ui/Note'
import { SectionHeading } from '@/console/ui/SectionHeading'
import { SourceBadge } from '@/console/ui/SourceBadge'
import { Stack } from '@/console/ui/Stack'
import { Tag } from '@/console/ui/Tag'
import { GridPreviewRow, MAX_SLOTS, isSuccessResponse, parseGridPayload, toPreviewRows } from './defaultPriorityGrid'

type Submission = { request: DefaultPriorityGridRequest; response: DefaultPriorityGridResponse | null; error: PanelError | null }

const Preconditions = (): JSX.Element => (
  <Callout tone="warn">
    <b>반영 전 확인</b>
    <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
      <li>디폴트(override_date 없음) 값을 바꿔요. 지난 시각 슬롯을 포함해 저장 즉시 다음 출고지시부터 적용돼요.</li>
      <li>요청에 넣은 슬롯만 바뀌어요. 넣은 슬롯 안에서 빠진 센터는 priority 가 비워져요.</li>
      <li>그날 일자 override 가 있는 슬롯은 override 가 우선이라 실제 적용값이 그대로예요.</li>
    </ul>
  </Callout>
)

const PreviewTable = ({ rows }: { rows: GridPreviewRow[] }): JSX.Element => (
  <DataTable
    maxHeight={520}
    stickyHeader
    columns={[
      { key: 'startTime', label: '슬롯', mono: true },
      { key: 'priorityType', label: 'priorityType' },
      { key: 'enabled', label: '센터 우선순위' },
      { key: 'ranked', label: '센터 순위' },
      { key: 'cleared', label: '비움' },
    ]}
    rows={rows.map((row) => ({
      key: row.startTime,
      cells: {
        startTime: row.startTime.slice(0, 5),
        priorityType: row.priorityType,
        enabled: <Tag tone={row.warehousePriorityEnabled ? 'good' : 'neutral'}>{row.warehousePriorityEnabled ? '사용' : '미사용'}</Tag>,
        ranked: row.ranked.length > 0 ? row.ranked.map((w) => `${w.priority} ${w.warehouseCode}`).join(' > ') : '-',
        cleared: row.cleared.length > 0 ? row.cleared.join(', ') : '-',
      },
    }))}
  />
)

const SubmissionResult = ({ submission }: { submission: Submission | null }): JSX.Element | null => {
  if (!submission) return null
  const { request, response, error } = submission
  const target = `${request.shipperCode} · ${request.cutType} · 슬롯 ${request.slots.length}개`

  if (error) return <Callout tone="crit">{`${target} 반영 실패. ${error.message}`}</Callout>
  if (isSuccessResponse(response)) {
    return <Callout tone="good">{`${target} 반영했어요. 출고일정관리 화면에서 디폴트 값을 다시 조회해 확인하세요.`}</Callout>
  }
  const meta = response?.meta
  return <Callout tone="crit">{`${target} 반영 실패. ${meta?.errorCode ?? '응답 meta 없음'}${meta?.message ? ` · ${meta.message}` : ''}`}</Callout>
}

/**
 * LOGISPRD-15812 — 디폴트 센터 우선순위 일괄 반영(`s-obs-default-priority`) + 반영 결과(`s-obs-default-priority-result`).
 *
 * 출고일정관리 2.5 화면은 단건 `set-priority-type` 만 불러 지난 시각 슬롯을 못 바꾸고, 24슬롯을 한 번에 바꿀 길도 없다.
 * 이 패널은 `set-default-priority-grid` 요청 본문을 붙여 넣은 그대로 보낸다. 검증을 통과하면 미리보기를 보여 주고,
 * 확인창을 거쳐 반영한다. 응답을 기다리는 동안만 셸 탭을 잠근다.
 */
export const DefaultPriorityGridPanel = ({ onBusyChange }: { onBusyChange?: (busy: boolean) => void }): JSX.Element => {
  const [payloadText, setPayloadText] = useState('')
  const [pendingConfirm, setPendingConfirm] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submission, setSubmission] = useState<Submission | null>(null)
  useReportBusy(submitting, onBusyChange)

  const parsed = useMemo(() => parseGridPayload(payloadText), [payloadText])
  const previewRows = useMemo(() => (parsed.request ? toPreviewRows(parsed.request) : []), [parsed])
  const request = parsed.request
  const canSubmit = request !== null && !submitting

  const confirmSubmit = (): void => {
    setPendingConfirm(false)
    if (!request) return
    setSubmitting(true)
    setSubmission(null)

    requestSetDefaultPriorityGrid(request)
      .then((response) => setSubmission({ request, response, error: null }))
      .catch((err) => setSubmission({ request, response: null, error: toPanelError(err) }))
      .finally(() => setSubmitting(false))
  }

  return (
    <>
      <SectionHeading
        id="s-obs-default-priority"
        level={2}
        title="디폴트 우선순위 반영"
        badge={<SourceBadge tone="have">기존 · POST /set-default-priority-grid</SourceBadge>}
      />
      <Stack>
        <Preconditions />
        <Field label="요청 본문 JSON (shipperCode · cutType · slots)">
          <textarea
            rows={14}
            spellCheck={false}
            placeholder={'{\n  "shipperCode": "29CM",\n  "cutType": "B2C",\n  "slots": [\n    { "startTime": "00:00:00", "priorityType": "PACKING", "warehousePriorityEnabled": true,\n      "warehouses": [{ "warehouseCode": "PAJ01", "priority": 1 }] }\n  ]\n}'}
            value={payloadText}
            disabled={submitting}
            onChange={(event) => setPayloadText(event.target.value)}
          />
        </Field>
        {parsed.errors.length > 0 ? (
          <Callout tone="crit">
            <b>반영할 수 없어요</b>
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {parsed.errors.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          </Callout>
        ) : null}
        {request ? (
          <>
            <SectionHeading level={3} title="미리보기" count={`${request.shipperCode} · ${request.cutType} · 슬롯 ${request.slots.length}개`} />
            <PreviewTable rows={previewRows} />
          </>
        ) : null}
        <div>
          <Button disabled={!canSubmit} onClick={() => setPendingConfirm(true)}>
            {submitting ? '반영 중…' : '반영'}
          </Button>
        </div>
      </Stack>
      <Note>
        슬롯은 최대 {MAX_SLOTS}개, 한 트랜잭션으로 반영해요. 하나라도 거절되면 아무것도 바뀌지 않아요. 바꾸기 전 값을 남겨 두면 같은 화면으로 되돌릴 수 있어요.
      </Note>

      <SectionHeading id="s-obs-default-priority-result" level={2} title="반영 결과" />
      {submitting ? <Callout>반영 중… 응답이 올 때까지 탭을 옮길 수 없어요.</Callout> : <SubmissionResult submission={submission} />}

      <ConfirmDialog
        open={pendingConfirm}
        title="디폴트 우선순위를 반영할까요?"
        body={
          request ? (
            <>
              <p style={{ margin: 0 }}>
                {request.shipperCode} · {request.cutType} · 슬롯 {request.slots.length}개
              </p>
              <ul style={{ margin: '10px 0 0', paddingLeft: 18 }}>
                <li>저장 즉시 적용돼요. 지난 시각 슬롯도 바뀌어요.</li>
                <li>넣은 슬롯 안에서 빠진 센터는 priority 가 비워져요.</li>
              </ul>
            </>
          ) : null
        }
        confirmLabel="반영"
        danger
        onConfirm={confirmSubmit}
        onCancel={() => setPendingConfirm(false)}
      />
    </>
  )
}
